"use client";

import type React from "react";
import Image from "next/image";
import { Button } from "@/components/themes/ui/button";
import { Input } from "@/components/themes/ui/input";
import { ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/themes/ui/dropdown-menu";
import { MediaCarousel } from "@/components/themes/media-carousel";
import { ThreeDots } from "react-loader-spinner";
import Link from "next/link";
import { AxiosPost_Nocatch } from "@/utilities/axioscall";
import { AK } from "@/lib/api-config";
import { useUser } from "@/providers/userContext";
import { useDispatch } from "react-redux";
import { setUser } from "@/store/user/actions";
import { handleSetCartInfoStorageItems } from "@/utilities/cartManager";
import { AxiosError } from "axios";
import { motion, Variants } from "framer-motion";
import SvgIcons from "@/components/themes/SvgIcons";

export const revalidate = 60;
export const runtime = "edge";

export interface MediaItem {
  type: "image";
  src: string;
  alt?: string;
  query?: string;
}

interface FormErrors {
  mobile?: string;
  email?: string;
  otp?: string;
  country?: string;
}

export default function Login({
  storeinfo,
  storesdata,
  gateway_config,
  coupons_config,
  _ongatewayTypes,
  suppcount,
  countrylistdata,
  region,
}: {
  storeinfo: any;
  storesdata: any;
  gateway_config: any[];
  coupons_config: any[];
  _ongatewayTypes: any[];
  suppcount: any[];
  countrylistdata: any[];
  region?: Record<string, any>;
}) {
  const [activeTab, setActiveTab] = useState<"mobile" | "email">("mobile");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otpSentMobile, setOtpSentMobile] = useState(false);
  const [otpMobile, setOtpMobile] = useState<string[]>(["", "", "", ""]);
  const [isLoadingMobile, setIsLoadingMobile] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [otpSentEmail, setOtpSentEmail] = useState(false);
  const [otpEmail, setOtpEmail] = useState<string[]>(["", "", "", ""]);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const otpInputRefsMobile = useRef<(HTMLInputElement | null)[]>([]);
  const otpInputRefsEmail = useRef<(HTMLInputElement | null)[]>([]);
  const mobileInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const { cartinfoData, setCartInfoData, login, logout, isLoggedIn } = useUser();
  const dispatch = useDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [countryListData] = useState(countrylistdata);
  const [suppcountryListData] = useState(suppcount);
  const suppcountryListDataArray = Array.isArray(suppcountryListData) ? suppcountryListData : [];
  const countryListDataArray = Array.isArray(countryListData) ? countryListData : [];

  const defaultCountry = suppcountryListDataArray.find((c) => c?.default === 1)?.id || null;
  const [selectedCountry, setSelectedCountry] = useState(
    countryListDataArray.find(
      (c) => c?.id?.toString() === defaultCountry?.toString()
    ) || null
  );

  useEffect(() => {
    const findCountry = countryListDataArray.find(
      (c) => c?.id?.toString() === (selectedCountry?.id?.toString() || defaultCountry?.toString())
    );
    setSelectedCountry(findCountry || null);
  }, [defaultCountry, countryListDataArray, selectedCountry?.id]);

  // Auto-focus logic
  useEffect(() => {
    if (activeTab === "mobile" && !otpSentMobile) {
      mobileInputRef.current?.focus();
    } else if (activeTab === "email" && !otpSentEmail) {
      emailInputRef.current?.focus();
    } else if (activeTab === "mobile" && otpSentMobile) {
      otpInputRefsMobile.current[0]?.focus();
    } else if (activeTab === "email" && otpSentEmail) {
      otpInputRefsEmail.current[0]?.focus();
    }
  }, [activeTab, otpSentMobile, otpSentEmail]);

  const mediaItems: MediaItem[] = [
    { type: "image", src: "https://assetsvilva.blr1.cdn.digitaloceanspaces.com/MY0352EH2711/uploads/HufDmYquzQWaG0SonN55O9IjXrd5kht9tgTnZuFF.webp" },
    { type: "image", src: "https://assetsvilva.blr1.cdn.digitaloceanspaces.com/MY0352EH2711/uploads/QS9Rdv5aXDAaY9IlkqPFJ5juwPwwT9BKCWK9GC5m.webp" },
    { type: "image", src: "https://assetsvilva.blr1.cdn.digitaloceanspaces.com/MY0352EH2711/uploads/FXq6P7vwBF5qxBwcJu27fAwmlEVXB0ornAmBQYzP.webp" },
  ];

  const invalidMobileNumbers = ["1234567890", "0987654321", "1111111111", "2222666666"];

  const validateMobileNumber = (number: string): string => {
    if (number.length !== 10) return "Enter valid number";
    if (!/^\d{10}$/.test(number)) return "Only numbers are accepted";
    if (invalidMobileNumbers.includes(number)) return "Please enter the correct number";
    return "";
  };

  const validateEmail = (email: string): string => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
    return "";
  };

  const startCountdown = () => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setCountdown(120);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleApiError = (error: any, isMobile: boolean): FormErrors => {
    const err = error as AxiosError;
    const newErrors: FormErrors = {};

    if (err.response?.data) {
      const apiErrors = (err.response.data as any).errors;
      const apiMessage = (err.response.data as any).message;

      if (err.response.status === 422 && apiErrors) {
        if (apiErrors.mobile) newErrors.mobile = apiErrors.mobile[0];
        if (apiErrors.email) newErrors.email = apiErrors.email[0];
        if (apiErrors.mobile_prefix) newErrors.country = apiErrors.mobile_prefix[0];
        if (apiErrors.otp) newErrors.otp = apiErrors.otp[0];
      } else if (apiMessage) {
        newErrors[isMobile ? "mobile" : "email"] = apiMessage;
      } else {
        newErrors[isMobile ? "mobile" : "email"] = "An unexpected error occurred.";
      }
    } else {
      newErrors[isMobile ? "mobile" : "email"] = err.message || "Request failed.";
    }

    return newErrors;
  };

  const getItemKey = (item: any) => {
    return `${item.id}-${item.selectedSize || ''}-${item.selectedColor || ''}`;
  };

  const mergeCarts = (localCart: any, serverCart: any) => {
    if (!serverCart || !serverCart.products || serverCart.products.length === 0) {
      return localCart;
    }

    if (!localCart || !localCart.products || localCart.products.length === 0) {
      const flaggedServerProducts = serverCart.products.map((p: any) => ({ ...p, addedBeforeLogin: true }));
      return {
        ...serverCart,
        products: flaggedServerProducts,
      };
    }

    const localMap = new Map();
    localCart.products.forEach((item: any) => {
      const key = getItemKey(item);
      if (!localMap.has(key)) {
        localMap.set(key, { ...item, quantity: 0 });
      }
      localMap.get(key).quantity += item.cartQuantity || 1;
    });

    const mergedProducts = [...localCart.products];
    serverCart.products.forEach((serverItem: any) => {
      const key = getItemKey(serverItem);
      const existing = localMap.get(key);
      if (existing) {
        const existingIndex = mergedProducts.findIndex((p: any) => getItemKey(p) === key);
        if (existingIndex !== -1) {
          mergedProducts[existingIndex].cartQuantity = (mergedProducts[existingIndex].cartQuantity || 1) + (serverItem.cartQuantity || 1);
        }
      } else {
        mergedProducts.push({ ...serverItem, addedBeforeLogin: true });
      }
    });

    const subtotal = mergedProducts.reduce((sum: number, item: any) => sum + Number(item.sp || item.price) * (item.cartQuantity || 1), 0);
    const shipping = subtotal >= 299 ? 0 : 45;
    const cartQuantityTotal = mergedProducts.reduce((sum: number, item: any) => sum + (item.cartQuantity || 1), 0);

    return {
      ...localCart,
      cartcount: mergedProducts.length,
      cartquantitycount: cartQuantityTotal,
      products: mergedProducts,
      cartprice: subtotal,
      subtotal: subtotal.toFixed(2),
      mrptotal: mergedProducts.reduce((sum: number, item: any) => sum + Number(item.mrp || item.price) * (item.cartQuantity || 1), 0).toFixed(2),
      shipping: shipping,
      ordertotal: (subtotal + shipping).toFixed(2),
      total: subtotal.toFixed(2),
      shippingrate: shipping === 0 ? "..." : "45.00",
    };
  };

  const handleGenerateOtp = async () => {
    const isMobile = activeTab === "mobile";
    const error = isMobile ? validateMobileNumber(mobileNumber) : validateEmail(emailAddress);

    if (error) {
      setErrors({ [isMobile ? "mobile" : "email"]: error });
      return;
    }

    setErrors({});
    setSuccessMessage("");
    isMobile ? setIsLoadingMobile(true) : setIsLoadingEmail(true);

    try {
      const api = isMobile ? AK.REQUESTOTPAPI : AK.EMAILREQUESTOTPAPI;
      const payload = {
        [isMobile ? "mobile" : "email"]: isMobile ? mobileNumber : emailAddress,
        ...(isMobile && { mobile_prefix: selectedCountry?.code }),
        storeid: AK.STOREID,
        storecode: AK.STORECODE,
      };

      const response = await AxiosPost_Nocatch(api, payload, false);
      if (response?.data?.success) {
        isMobile ? setOtpSentMobile(true) : setOtpSentEmail(true);
        dispatch(setUser(null));
        startCountdown();
      } else {
        setErrors({ [isMobile ? "mobile" : "email"]: response?.data?.message || "Unauthorized access." });
      }
    } catch (error) {
      setErrors(handleApiError(error, isMobile));
    } finally {
      isMobile ? setIsLoadingMobile(false) : setIsLoadingEmail(false);
    }
  };

  const handleVerifyOtp = async () => {
    const isMobile = activeTab === "mobile";
    const otp = (isMobile ? otpMobile : otpEmail).join("");
    const setLoading = isMobile ? setIsLoadingMobile : setIsLoadingEmail;

    if (otp.length !== 4) {
      setErrors({ otp: "Please enter a valid 4-digit OTP" });
      return;
    }

    setErrors({});
    setSuccessMessage("");
    setLoading(true);

    try {
      const api = isMobile ? AK.VERIFYOTPAPI : AK.EMAILVERIFYOTPAPI;
      const payload = {
        [isMobile ? "mobile" : "email"]: isMobile ? mobileNumber : emailAddress,
        ...(isMobile && { mobile_prefix: selectedCountry?.code }),
        otp,
        storeid: AK.STOREID,
        storecode: AK.STORECODE,
      };

      const response = await AxiosPost_Nocatch(api, payload, false);
      if (response?.data?.success) {
        setSuccessMessage("Login Successful");
        const userdata = response.data; // No deep clone
        login(userdata);
        dispatch(setUser(userdata));

        // Merge cart
        console.log("userdata.cartinfo", userdata.cartinfo)
        if (response.data.cartinfo) {
          const parsed = typeof userdata.cartinfo === "string" ? JSON.parse(userdata.cartinfo) : userdata.cartinfo;
          const merged = mergeCarts(cartinfoData, parsed);
          setCartInfoData(merged);
          handleSetCartInfoStorageItems(merged);
        }
        console.log(`${isMobile ? "Mobile" : "Email"} OTP verified successfully`, response.data);
        const redirectTo = decodeURIComponent(searchParams.get("from") || "/");
        window.location.href = redirectTo;
      } else {
        setErrors({ otp: response?.data?.message || "Invalid OTP or unauthorized access." });
      }
    } catch (error) {
      setErrors(handleApiError(error, isMobile));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const isMobile = activeTab === "mobile";
    setErrors({});
    setSuccessMessage("");
    isMobile ? setOtpMobile(["", "", "", ""]) : setOtpEmail(["", "", "", ""]);

    try {
      const api = isMobile ? AK.REQUESTOTPAPI : AK.EMAILREQUESTOTPAPI;
      const payload = {
        [isMobile ? "mobile" : "email"]: isMobile ? mobileNumber : emailAddress,
        ...(isMobile && { mobile_prefix: selectedCountry?.code }),
        storeid: AK.STOREID,
        storecode: AK.STORECODE,
      };

      const response = await AxiosPost_Nocatch(api, payload, false);
      if (response?.data?.success) {
        startCountdown();
        console.log(`OTP resent to ${isMobile ? "mobile" : "email"}`);
      } else {
        setErrors({ [isMobile ? "mobile" : "email"]: response?.data?.message || "Failed to resend OTP." });
      }
    } catch (error) {
      setErrors(handleApiError(error, isMobile));
    }
  };

  const handleLogout = async () => {
    try {
      await AxiosPost_Nocatch(AK.LOGOUTAPI, {}, true);
      logout();
      router.push("/signin");
    } catch (error) {
      console.error("Logout failed:", error);
      logout();
      router.push("/signin");
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, type: "mobile" | "email") => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;

    const newOtp = type === "mobile" ? [...otpMobile] : [...otpEmail];
    newOtp[index] = value.slice(0, 1);

    if (type === "mobile") {
      setOtpMobile(newOtp);
      if (value && index < 3) otpInputRefsMobile.current[index + 1]?.focus();
    } else {
      setOtpEmail(newOtp);
      if (value && index < 3) otpInputRefsEmail.current[index + 1]?.focus();
    }
    setErrors((prev) => ({ ...prev, otp: undefined }));
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number, type: "mobile" | "email") => {
    const currentOtp = type === "mobile" ? otpMobile : otpEmail;
    const currentRefs = type === "mobile" ? otpInputRefsMobile : otpInputRefsEmail;

    if (e.key === "Backspace" && !currentOtp[index] && index > 0) {
      currentRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter" && currentOtp.every((d) => d !== "")) {
      handleVerifyOtp();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number, type: "mobile" | "email") => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("Text").trim();
    if (!/^\d+$/.test(pasted)) return;

    const newOtp = type === "mobile" ? [...otpMobile] : [...otpEmail];
    for (let i = 0; i < pasted.length && index + i < 4; i++) {
      newOtp[index + i] = pasted[i];
    }

    if (type === "mobile") {
      setOtpMobile(newOtp);
    } else {
      setOtpEmail(newOtp);
    }

    const focusIndex = Math.min(index + pasted.length, 3);
    const refs = type === "mobile" ? otpInputRefsMobile : otpInputRefsEmail;
    refs.current[focusIndex]?.focus();
  };

  const formatTime = (seconds: number) => `${seconds}`;

  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    const isMobile = activeTab === "mobile";
    const currentOtp = isMobile ? otpMobile : otpEmail;
    if (currentOtp.every((d) => d !== "")) {
      handleVerifyOtp();
    }
  }, [otpMobile, otpEmail, activeTab]);

  useEffect(() => {
    setOtpSentMobile(false);
    setOtpMobile(["", "", "", ""]);
    setIsLoadingMobile(false);
    setMobileNumber("");
    setOtpSentEmail(false);
    setOtpEmail(["", "", "", ""]);
    setIsLoadingEmail(false);
    setEmailAddress("");
    setSuccessMessage("");
    setErrors({});
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setCountdown(120);
  }, [activeTab]);

  const inputVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const buttonVariants: Variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
  };

  return (
    <div className="flex max-h-full md:h-screen md:items-center items-stretch md:justify-center justify-end bg-gray-100 md:p-8">
      <div className="w-full max-w-5xl overflow-hidden md:rounded-[6px] md:shadow-lg md:grid md:grid-cols-2">
        <div className="h-[600px] md:h-[600px] bg-dark-green-bg">
          <MediaCarousel media={mediaItems} />
        </div>

        <motion.div
          className="flex flex-col items-center md:relative absolute bottom-0 right-0 left-0 rounded-tl-[12px] rounded-tr-[12px] justify-center bg-light-green-bg p-6 md:p-10 bg-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <Link href="/">
              <img
                src={
                  process.env.NEXT_PUBLIC_STORE_CODE
                    ? `https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/${process.env.NEXT_PUBLIC_STORE_CODE}/logos/logowebp.webp`
                    : "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp"
                }
                onError={(e) => {
                  e.currentTarget.src =
                    "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp";
                }}
                alt="logo"
                width={240}
                height={120}
                className="mx-auto h-[120px] w-[240px] object-contain"
              />
            </Link>
          </div>

          {isLoggedIn ? (
            <motion.div
              className="w-full max-w-xs space-y-6 text-center"
              initial="hidden"
              animate="visible"
              variants={inputVariants}
            >
              <p className="text-lg font-medium text-gray_text">You are logged in!</p>
              <div className="flex w-full items-center gap-3">
                <motion.div className="w-full" variants={buttonVariants}>
                  <Link href="/">
                    <Button className="w-full bg-button-green text-white bg-primary h-[52px] rounded-[6px] hover:bg-button-green/90">
                      Go to Home
                    </Button>
                  </Link>
                </motion.div>
                <motion.div className="w-full" variants={buttonVariants}>
                  <Link href="/my-account">
                    <Button className="w-full bg-button-green text-white bg-primary h-[52px] rounded-[6px] hover:bg-button-green/90">
                      My Account
                    </Button>
                  </Link>
                </motion.div>
              </div>
              <motion.div variants={buttonVariants}>
                <Button
                  className="w-full bg-red-600 py-2 text-white h-[52px] rounded-[6px] hover:bg-red-700"
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              </motion.div>
            </motion.div>
          ) : (
            <>
              <motion.div
                className="mb-8 flex space-x-8"
                initial="hidden"
                animate="visible"
                variants={inputVariants}
              >
                <button
                  className={`flex items-center space-x-2 text-lg font-medium ${activeTab === "mobile"
                    ? "text-active-tab-green border-b-2 border-primary"
                    : "text-gray_text border-b-2 border-b-transparent"
                    }`}
                  onClick={() => setActiveTab("mobile")}
                >
                  <SvgIcons.LoginMobile className={activeTab === "email" ? "text-black" : "text-primary"} />
                  <span className={activeTab === "email" ? "text-black" : "text-primary"}>Mobile</span>
                </button>
                <button
                  className={`flex items-center space-x-2 text-lg font-medium ${activeTab === "email"
                    ? "text-active-tab-green border-b-2 border-primary"
                    : "text-gray_text"
                    }`}
                  onClick={() => setActiveTab("email")}
                >
                  <SvgIcons.LoginEmail className={activeTab === "mobile" ? "text-black" : "text-primary"} />
                  <span className={activeTab === "mobile" ? "text-black" : "text-primary"}>Email</span>
                </button>
              </motion.div>

              {activeTab === "mobile" && (
                <motion.div
                  className="w-full max-w-xs space-y-6"
                  initial="hidden"
                  animate="visible"
                  variants={inputVariants}
                >
                  {otpSentMobile && (
                    <motion.div
                      className="flex items-center justify-center space-x-[8px] text-sm text-gray_text"
                      variants={inputVariants}
                    >
                      <p className="text-[14px]">OTP sent to your Mobile number</p>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setOtpSentMobile(false)}
                        className="h-6 w-6 rounded-[6px]"
                      >
                        <SvgIcons.LoginEditIcon className="text-primary" />
                      </Button>
                    </motion.div>
                  )}
                  <motion.div className="space-y-2" variants={inputVariants}>
                    {!otpSentMobile && (
                      <label htmlFor="mobile-number" className="text-sm font-medium text-gray_text">
                        Mobile number
                      </label>
                    )}
                    <div
                      className={`flex border-solid border-2 items-center h-[52px] rounded-[6px] ${errors.mobile || errors.country ? "border-error" : "border-[#CECECE]"
                        } bg-white px-3 py-2 focus-within:border-primary focus-within:border-active-tab-primary`}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="flex focus:ring-0 outline-none border-none items-center p-0 h-auto">
                            <Image
                              src={process.env.NEXT_PUBLIC_ECOMSAASASSETS_COUNTRIES + selectedCountry?.flag}
                              alt={`${selectedCountry?.country} flag`}
                              width={20}
                              height={20}
                              className=""
                              unoptimized
                            />
                            <span className="text-sm font-medium text-gray_text">{selectedCountry?.calling_code}</span>
                            <ChevronDown className="h-4 w-4 text-gray_text" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-white rounded-[6px]">
                          {countryListData.map((country) => (
                            suppcountryListData?.find((supp) => supp?.id?.toString() === country?.id?.toString()) && (
                              <DropdownMenuItem
                                key={country.code}
                                onSelect={() => setSelectedCountry(country)}
                                className="flex items-center"
                              >
                                <div className="country-code-item" style={{ display: "flex" }}>
                                  <img
                                    style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }}
                                    src={process.env.NEXT_PUBLIC_ECOMSAASASSETS_COUNTRIES + country?.flag}
                                    alt={`${country?.country} flag`}
                                  />
                                  &nbsp;&nbsp;&nbsp;
                                  <span className="text-[14px]">{country?.calling_code}</span>
                                  &nbsp;&nbsp;&nbsp;
                                  <span className="text-[14px]">{country?.country}</span>
                                </div>
                                <hr />
                              </DropdownMenuItem>
                            )
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <motion.div variants={inputVariants}>
                        <Input
                          id="mobile-number"
                          type="tel"
                          placeholder={otpSentMobile ? mobileNumber : "Whatsapp number"}
                          value={mobileNumber}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                            setMobileNumber(value);
                            setErrors((prev) => ({ ...prev, mobile: undefined }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !isLoadingMobile) handleGenerateOtp();
                          }}
                          className="flex-1 !bg-transparent border-none rounded-[6px] !bg-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                          disabled={otpSentMobile}
                          ref={mobileInputRef}
                        />
                      </motion.div>
                    </div>
                    {(errors.mobile || errors.country) && (
                      <p className="text-sm text-error">{errors.mobile || errors.country}</p>
                    )}
                    {successMessage && activeTab === "mobile" && (
                      <p className="text-sm text-green-600 font-medium">{successMessage}</p>
                    )}
                  </motion.div>
                  {!otpSentMobile ? (
                    <motion.div variants={buttonVariants}>
                      <Button
                        className="w-full bg-button-green text-white bg-primary h-[52px] rounded-[6px] hover:bg-button-green/90"
                        onClick={handleGenerateOtp}
                        disabled={isLoadingMobile}
                      >
                        {isLoadingMobile ? (
                          <ThreeDots
                            visible={true}
                            height="120"
                            width="120"
                            color="#fff"
                            radius="9"
                            ariaLabel="three-dots-loading"
                            wrapperStyle={{}}
                            wrapperClass=""
                          />
                        ) : (
                          "Generate OTP"
                        )}
                      </Button>
                    </motion.div>
                  ) : (
                    <>
                      <motion.div className="flex justify-center space-x-2" variants={inputVariants}>
                        {otpMobile.map((digit, index) => (
                          <motion.div key={index} variants={inputVariants}>
                            <Input
                              type="tel"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(e, index, "mobile")}
                              onKeyDown={(e) => handleOtpKeyDown(e, index, "mobile")}
                              onPaste={(e) => handleOtpPaste(e, index, "mobile")}
                              ref={(el: any) => (otpInputRefsMobile.current[index] = el)}
                              className={`h-[52px] md:w-[70px] w-[60px] text-center text-lg font-bold rounded-[6px] bg-white border-2 ${errors.otp ? "border-error" : "border-[#DEDDDD]"
                                } focus:border-active-tab-primary focus:border-primary focus:rong-0 focus:ring-transparent `}
                            />
                          </motion.div>
                        ))}
                      </motion.div>
                      {errors.otp && <p className="text-sm text-error text-center">{errors.otp}</p>}
                      <motion.div className="flex justify-end items-center space-x-[5px] text-sm" variants={inputVariants}>
                        <Button
                          variant="link"
                          className={`p-0 h-auto ${countdown > 0 ? "text-[#666666]" : "text-primary"} hover:text-primary`}
                          onClick={handleResendOtp}
                          disabled={countdown > 0}
                        >
                          Resend OTP
                        </Button>
                        {countdown > 0 && <span className="text-gray_text">{formatTime(countdown)} Sec</span>}
                      </motion.div>
                      <motion.div variants={buttonVariants}>
                        <Button
                          className="w-full bg-button-green text-white bg-primary !rounded-[6px] !h-[52px] hover:bg-button-green/90"
                          onClick={handleVerifyOtp}
                          disabled={isLoadingMobile}
                        >
                          {isLoadingMobile ? (
                            <ThreeDots
                              visible={true}
                              height="120"
                              width="120"
                              color="#fff"
                              radius="9"
                              ariaLabel="three-dots-loading"
                              wrapperStyle={{}}
                              wrapperClass=""
                            />
                          ) : (
                            "Verify Account"
                          )}
                        </Button>
                      </motion.div>
                    </>
                  )}
                </motion.div>
              )}
              {activeTab === "email" && (
                <motion.div
                  className="w-full max-w-xs space-y-6"
                  initial="hidden"
                  animate="visible"
                  variants={inputVariants}
                >
                  {otpSentEmail && (
                    <motion.div
                      className="flex items-center justify-center space-x-[8px] text-sm text-gray_text"
                      variants={inputVariants}
                    >
                      <span>OTP sent to your Email address</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setOtpSentEmail(false)}
                        className="h-6 w-6 rounded-[6px] "
                      >
                        <SvgIcons.LoginEditIcon className="text-primary" />
                      </Button>
                    </motion.div>
                  )}
                  <motion.div className="space-y-2" variants={inputVariants}>
                    {!otpSentEmail && (
                      <label htmlFor="email-address" className="text-sm font-medium text-gray_text">
                        Email address
                      </label>
                    )}
                    <motion.div
                      variants={inputVariants}
                      className={`flex border-solid border-2 items-center h-[52px] rounded-[6px] ${errors.email ? "border-error" : "border-[#CECECE]"
                        } bg-white px-3 py-2 focus-within:border-primary focus-within:border-active-tab-primary`}
                    >
                      <Input
                        id="email-address"
                        type="email"
                        placeholder={otpSentEmail ? emailAddress : "Enter email"}
                        value={emailAddress}
                        onChange={(e) => {
                          setEmailAddress(e.target.value);
                          setErrors((prev) => ({ ...prev, email: undefined }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isLoadingEmail) handleGenerateOtp();
                        }}
                        className="border-none outline-none focus:border-none focus:outline-none ring-0 focus:border-primary focus:ring-transparent !shadow-none"
                        disabled={otpSentEmail}
                        ref={emailInputRef}
                      />
                    </motion.div>
                    {errors.email && <p className="text-sm text-error">{errors.email}</p>}
                    {successMessage && activeTab === "email" && (
                      <p className="text-sm text-green-600 font-medium">{successMessage}</p>
                    )}
                  </motion.div>
                  {!otpSentEmail ? (
                    <motion.div variants={buttonVariants}>
                      <Button
                        className="w-full bg-button-green text-white bg-primary h-[52px] rounded-[6px] hover:bg-button-green/90"
                        onClick={handleGenerateOtp}
                        disabled={isLoadingEmail}
                      >
                        {isLoadingEmail ? (
                          <ThreeDots
                            visible={true}
                            height="120"
                            width="120"
                            color="#fff"
                            radius="9"
                            ariaLabel="three-dots-loading"
                            wrapperStyle={{}}
                            wrapperClass=""
                          />
                        ) : (
                          "Generate OTP"
                        )}
                      </Button>
                    </motion.div>
                  ) : (
                    <>
                      <motion.div className="flex justify-center space-x-2" variants={inputVariants}>
                        {otpEmail.map((digit, index) => (
                          <motion.div key={index} variants={inputVariants}>
                            <Input
                              type="tel"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(e, index, "email")}
                              onKeyDown={(e) => handleOtpKeyDown(e, index, "email")}
                              onPaste={(e) => handleOtpPaste(e, index, "email")}
                              ref={(el: any) => (otpInputRefsEmail.current[index] = el)}
                              className={`h-[52px] md:w-[70px] w-[60px] text-center text-lg font-bold rounded-[6px] bg-white border-2 ${errors.otp ? "border-error" : "border-[#DEDDDD]"
                                } focus:border-active-tab-primary focus:border-primary focus:rong-0 focus:ring-transparent `}
                            />
                          </motion.div>
                        ))}
                      </motion.div>
                      {errors.otp && <p className="text-sm text-error text-center">{errors.otp}</p>}
                      <motion.div className="flex justify-end items-center space-x-[5px] text-sm" variants={inputVariants}>
                        <Button
                          variant="link"
                          className={`p-0 h-auto ${countdown > 0 ? "text-[#666666]" : "text-primary"} hover:text-primary`}
                          onClick={handleResendOtp}
                          disabled={countdown > 0}
                        >
                          Resend OTP
                        </Button>
                        {countdown > 0 && <span className="text-gray_text">{formatTime(countdown)} Sec</span>}
                      </motion.div>
                      <motion.div variants={buttonVariants}>
                        <Button
                          className="w-full bg-button-green text-white bg-primary !rounded-[6px] !h-[52px] hover:bg-button-green/90"
                          onClick={handleVerifyOtp}
                          disabled={isLoadingEmail}
                        >
                          {isLoadingEmail ? (
                            <ThreeDots
                              visible={true}
                              height="120"
                              width="120"
                              color="#fff"
                              radius="9"
                              ariaLabel="three-dots-loading"
                              wrapperStyle={{}}
                              wrapperClass=""
                            />
                          ) : (
                            "Verify Account"
                          )}
                        </Button>
                      </motion.div>
                    </>
                  )}
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}